import { useDispatch, useSelector } from 'react-redux';
import { styled, SwipeableDrawer, useMediaQuery } from '@mui/material';
import GlobalStyles from '@mui/material/GlobalStyles';

import { navbarCloseMobile } from 'app/store/fuse/navbarSlice';
import NavbarStyle3Content from './NavbarStyle3Content';

interface NavbarProps {
	dense: boolean;
	open: boolean;
	folded: boolean;
	position: 'left' | 'right';
}

const navbarWidth = 120;
const navbarWidthDense = 64;
const panelWidth = 280;

const StyledNavBar = styled('div')<NavbarProps>(({ theme, dense, open, folded, position }) => ({
	minWidth: navbarWidth,
	width: navbarWidth,
	maxWidth: navbarWidth,
	...(dense && {
		minWidth: navbarWidthDense,
		width: navbarWidthDense,
		maxWidth: navbarWidthDense,
		...(!open && {
			...(position === 'left' && {
				marginLeft: -navbarWidthDense
			}),
			...(position === 'right' && {
				marginRight: -navbarWidthDense
			})
		})
	}),
	...(!folded && {
		minWidth: dense ? navbarWidthDense + panelWidth : navbarWidth + panelWidth,
		width: dense ? navbarWidthDense + panelWidth : navbarWidth + panelWidth,
		maxWidth: dense ? navbarWidthDense + panelWidth : navbarWidth + panelWidth,
		'& #fuse-navbar-panel': {
			opacity: '1!important',
			pointerEvents: 'initial!important'
		},
		...(!open && {
			...(position === 'left' && {
				marginLeft: -(dense ? navbarWidthDense + panelWidth : navbarWidth + panelWidth)
			}),
			...(position === 'right' && {
				marginRight: -(dense ? navbarWidthDense + panelWidth : navbarWidth + panelWidth)
			})
		})
	}),
	...(!open && {
		transition: theme.transitions.create('margin', {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.leavingScreen
		}),
		...(position === 'left' && {
			marginLeft: -(dense ? navbarWidthDense : navbarWidth)
		}),
		...(position === 'right' && {
			marginRight: -(dense ? navbarWidthDense : navbarWidth)
		})
	}),
	...(open && {
		transition: theme.transitions.create('margin', {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen
		})
	})
}));

const StyledNavBarMobile = styled(SwipeableDrawer)(({ theme }) => ({
	'& .MuiDrawer-paper': {
		'& #fuse-navbar-side-panel': {
			minWidth: 'auto',
			wdith: 'auto'
		},
		'& #fuse-navbar-panel': {
			opacity: '1!important',
			pointerEvents: 'initial!important'
		}
	}
}));

function NavbarStyle3({ dense = false }: { dense?: boolean }) {
	const dispatch = useDispatch();
	const isLgUp = useMediaQuery(theme => theme.breakpoints.up('lg'));

	const config = useSelector(({ fuse }: any) => fuse.settings.current.layout.config);
	const navbar = useSelector(({ fuse }: any) => fuse.navbar);
	const { folded } = config.navbar;

	return (
		<>
			<GlobalStyles
				styles={theme => ({
					'& #fuse-navbar-side-panel': {
						width: dense ? navbarWidthDense : navbarWidth,
						minWidth: dense ? navbarWidthDense : navbarWidth,
						maxWidth: dense ? navbarWidthDense : navbarWidth
					},
					'& #fuse-navbar-panel': {
						maxWidth: '100%',
						width: panelWidth,
						[theme.breakpoints.up('lg')]: {
							minWidth: panelWidth,
							maxWidth: 'initial'
						}
					}
				})}
			/>
			{isLgUp ? (
				<StyledNavBar
					open={navbar.open}
					dense={dense}
					folded={folded}
					position={config.navbar.position}
					className="sticky top-0 z-20 h-screen flex-auto shrink-0 flex-col shadow-5"
				>
					<NavbarStyle3Content dense={dense} folded={folded} />
				</StyledNavBar>
			) : (
				<StyledNavBarMobile
					classes={{
						paper: 'flex-col flex-auto h-screen max-w-full w-auto overflow-hidden'
					}}
					anchor={config.navbar.position}
					variant="temporary"
					open={navbar.mobileOpen}
					onClose={() => dispatch(navbarCloseMobile())}
					onOpen={() => {}}
					disableSwipeToOpen
					ModalProps={{
						keepMounted: true // Better open performance on mobile.
					}}
				>
					<NavbarStyle3Content dense={dense} folded={folded} />
				</StyledNavBarMobile>
			)}
		</>
	);
}

export default NavbarStyle3;
